import { useParams } from 'react-router-dom';
import { TabData } from '@medtronic/pecc-react-component-library-js';
import { useGetDatasetFilesQuery, useGetDatasetQuery } from '~/api/v2/hooks';
import { UploadingFile } from '~/hooks/dataset/useDatasetFileUploader';
import { DatasetDetailsPanel, DatasetFilesPanel, ManageDatasetAccessPanel } from './components';

export enum DatasetDetailsTabsEnum {
  DETAILS = 0,
  DATA_FILES = 1,
  SUPPORTIVE_DOCS = 2,
  MANAGE_ACCESS = 3,
}

type BuildTabsDataParams = {
  isManageAccessAllowed: boolean;
  uploadingDataFiles: Map<string, UploadingFile>;
  uploadingSupportiveDocs: Map<string, UploadingFile>;
};

export const useBuildTabsData = ({
  isManageAccessAllowed,
  uploadingDataFiles,
  uploadingSupportiveDocs,
}: BuildTabsDataParams) => {
  const datasetId = +useParams().id!;

  const { data } = useGetDatasetQuery({ datasetId });
  const { data: allDataFiles } = useGetDatasetFilesQuery({
    datasetId,
    supportiveDocs: false,
  });
  const { data: allSupportiveDocuments } = useGetDatasetFilesQuery({
    datasetId,
    supportiveDocs: true,
  });

  const tabs = [
    {
      key: DatasetDetailsTabsEnum.DETAILS,
      label: 'Dataset details',
      content: <DatasetDetailsPanel />,
    },
  ];

  if (!!data?.source_docs_upload_allowed && !!allDataFiles?.length) {
    tabs.push({
      key: DatasetDetailsTabsEnum.DATA_FILES,
      label: 'Data',
      content: <DatasetFilesPanel uploadingFiles={uploadingDataFiles} />,
    });
  }

  if (allSupportiveDocuments?.length) {
    tabs.push({
      key: DatasetDetailsTabsEnum.SUPPORTIVE_DOCS,
      label: 'Supportive documents',
      content: <DatasetFilesPanel uploadingFiles={uploadingSupportiveDocs} supportiveDocs />,
    });
  }

  if (isManageAccessAllowed) {
    tabs.push({
      key: DatasetDetailsTabsEnum.MANAGE_ACCESS,
      label: 'Manage dataset access',
      content: <ManageDatasetAccessPanel />,
    });
  }

  return tabs;
};

export const getSelectedTabIndex = (
  tabs: Array<TabData & { key: DatasetDetailsTabsEnum }>,
  activeTab: DatasetDetailsTabsEnum,
) =>
  Math.max(
    0,
    tabs.findIndex((tab) => tab.key === activeTab),
  );

export const getRedirectTabKey = (
  hasDataFiles: boolean,
  hasSupportiveFiles: boolean,
  activeTab: DatasetDetailsTabsEnum,
) => {
  if (!hasDataFiles && hasSupportiveFiles) {
    return DatasetDetailsTabsEnum.SUPPORTIVE_DOCS;
  }

  if (!hasSupportiveFiles && hasDataFiles) {
    return DatasetDetailsTabsEnum.DATA_FILES;
  }

  if (hasDataFiles && hasSupportiveFiles && activeTab === DatasetDetailsTabsEnum.DETAILS) {
    return DatasetDetailsTabsEnum.DATA_FILES;
  }

  return null;
};
